import { LanguageKey } from "@/types/generated/LanguageKey";
import { MappedStateDto } from "@apis/stateMappingClient/stateMappingClient.dto";
import { userLanguage } from "@/utils/languageUtil";

/**
 * STIC-16316: the platform languages a state label can be given in, with the
 * key of their name as the user settings show it
 */
export const STATE_LABEL_LANGUAGES: { code: string; name: LanguageKey }[] = [
  { code: "en", name: LanguageKey.language_en },
  { code: "nl", name: LanguageKey.language_nl },
  { code: "de", name: LanguageKey.language_de },
  { code: "fr", name: LanguageKey.language_fr },
  { code: "es", name: LanguageKey.language_es },
  { code: "it", name: LanguageKey.language_it },
  { code: "pt", name: LanguageKey.language_pt },
];

/** a state's label in a language: its translation, else its label */
export const stateLabelIn = (state: MappedStateDto, language: string): string =>
  state.translations?.[language]?.trim() || state.label;

/**
 * the label stored with a tracker's current state, in the user's language when the state has one
 * in it (the labels per language are stored next to it), else the default label
 */
export const storedStateLabel = (
  label?: string | null,
  labels?: Record<string, string> | null
): string | undefined => labels?.[userLanguage()]?.trim() || label || undefined;

/** what the user sees for a state: its label in the user's language, else its key */
export const stateLabel = (state: MappedStateDto): string =>
  stateLabelIn(state, userLanguage()) || state.key;

/**
 * a state's labels per language as typed, trimmed, without the empty ones and without the ones that read
 * like the default label (they would hide a later change of it); null when none is left
 */
export const cleanTranslations = (
  translations?: Record<string, string> | null,
  defaultLabel?: string | null
): Record<string, string> | null => {
  const sameAsDefault = (label: string) => label === (defaultLabel || "").trim();
  const kept = Object.fromEntries(
    Object.entries(translations || {})
      .map(([code, label]) => [code, (label || "").trim()])
      .filter(([, label]) => label && !sameAsDefault(label))
  );
  return Object.keys(kept).length ? kept : null;
};

/** the suggested labels put into the languages that are still empty; typed ones stay */
export const fillEmpty = (
  translations: Record<string, string> | null | undefined,
  suggested: Record<string, string>
): Record<string, string> => {
  const next = { ...(translations || {}) };
  Object.entries(suggested).forEach(([code, label]) => {
    if (!next[code]?.trim()) next[code] = label;
  });
  return next;
};

/** the language codes a state has its own label in, in the platform order */
export const translatedCodes = (translations?: Record<string, string> | null): string[] =>
  STATE_LABEL_LANGUAGES.map(({ code }) => code).filter((code) => translations?.[code]?.trim());
